import React, { useState, useMemo } from 'react'
import { useStyles } from './DetailedViewPrivateOffer.styles'
import {
  OfferRecipients,
  OfferStakeholders,
  OfferStatus,
  PrivateOffer,
} from '../../../modules/privateOffer/types'
import { Grid, Typography } from '@mui/material'
import { PlanType, useAuth } from '@labrav/react-components'
import { isEmpty } from 'lodash'
import {
  ContainerPricingModel,
  Product,
  CloudMarketplace,
} from '../../../modules/productsListing/types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import {
  ShortenedNameV2,
  useCurrentCloudProvider,
  useAccountId,
  CloudAccountProvider,
  providerToAuthCloud,
  LoadingTypes,
  isLoading as Loading,
  SelectedCloudIcon,
} from '@labrav/common'
import { useNavigate, useParams } from 'react-router-dom'
import clsx from 'clsx'
import { EulaView } from '../../../common/components/EulaView/EulaView'
import { CustomMetaDataView } from '../../../common/components/CustomMetaDataView/CustomMetaDataView'
import { ViewRecipients } from '../../../common/components/ViewRecipients/ViewRecipients'
import { ViewStakeholders } from '../../../common/components/ViewStakeholders/ViewStakeholders'
import type { StakeholderUserOption } from '../../../common/components/ViewStakeholders/stakeholderUserOption'
import { StatusStages } from '../../../common/components/StatusStages/StatusStages'
import { AWSOfferHeader } from '../../../common/components/OfferHeader/AWSOfferHeader'
import { MicrosoftOfferHeader } from '../../../common/components/OfferHeader/MicrosoftOfferHeader'
import type { PrivateOfferViewTrackingHandlers } from '../../../common/components/OfferHeader/OfferHeaderActions/OfferHeaderActions'
import { AWSOfferInfo } from '../../../common/components/CloudBasedOfferInfo/AWSOfferInfo'
import { AWSAMIOfferInfo } from '../../../common/components/CloudBasedOfferInfo/AWSAMIOfferInfo'
import { MicrosoftOfferInfo } from '../../../common/components/CloudBasedOfferInfo/MicrosoftOfferInfo'
import { updatePrivateOfferForUser } from '../../../modules/privateOffer/action'
import { useDispatch, useSelector } from '../../../store'
import { getCurrencySymbol } from '../../../common/utils/getCurrencySymbol'
import {
  CRMTypes,
  addRecipientsPrivateOfferToolTipMessage,
  addStakeholdersPrivateOfferToolTipMessage,
} from '../../../utils/constants'

export type DetailedViewPrivateOfferProps = {
  privateOffer: PrivateOffer
  planType?: PlanType
  externalBaseURL?: string
  contractPricingModel?: ContainerPricingModel
  crmOpportunityId?: string
  cloudMarketplace?: CloudMarketplace
  search?: string
  product?: Product
  crmType?: CRMTypes
  onBack?: () => void
  partnerId?: string
  users?: StakeholderUserOption[]
  viewTracking?: PrivateOfferViewTrackingHandlers
}

export const DetailedViewPrivateOffer: React.FC<
  DetailedViewPrivateOfferProps
> = ({
  privateOffer,
  planType,
  product,
  crmOpportunityId,
  cloudMarketplace,
  externalBaseURL,
  search,
  crmType,
  onBack,
  partnerId,
  users = [],
  viewTracking,
}) => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {
    cloud: routeCloud,
    accountId,
    partnerId: partnerIdFromParams,
  } = useParams<{
    cloud: string
    accountId: string
    partnerId: string
  }>()

  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const routeAccountId =
    partnerId || accountId || partnerIdFromParams || fallbackAccountId || ''

  const privateOffersListURL =
    routeCloud && routeAccountId
      ? `/flyout/${routeCloud}/${routeAccountId}/offers-authorizations/private-offers`
      : '/flyout/offers-authorizations/private-offers'

  const isLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.GENERAL)
  )

  const { loading: authLoading, canDo } = useAuth()
  const cloud = useCurrentCloudProvider()
  const canEditOffer = useMemo(() => {
    return (
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(cloud.toLowerCase() as CloudAccountProvider),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, cloud])

  const permissionMessageString =
    'This action is disabled because you have read-only access'
  const [openEditRecipient, setOpenEditRecipient] = useState(false)
  const [openEditStakeHolders, setOpenEditStakeholders] = useState(false)

  const handleCloseRecipients = () => setOpenEditRecipient(false)
  const handleCloseStakeholders = () => setOpenEditStakeholders(false)
  const offerMetaData = privateOffer?.metaData || []

  const hasOfferMetaData = !!offerMetaData?.length

  const offerStatus = privateOffer?.offerStatus

  const isRecipientDisabled = [
    OfferStatus.PRIVATE_OFFER_FAILED,
    OfferStatus.PRIVATE_OFFER_EXPIRED,
    OfferStatus.PRIVATE_OFFER_RESTRCITED,
    OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,

    OfferStatus.OFFER_CREATION_FAILED,
    OfferStatus.OFFER_EXPIRED,
    OfferStatus.OFFER_RESTRICTED,
    OfferStatus.OFFER_RESTRICTION_IN_PROGRESS,
  ].includes(offerStatus)

  const isStakeHolderDisabled = [
    OfferStatus.PRIVATE_OFFER_FAILED,
    OfferStatus.PRIVATE_OFFER_EXPIRED,
    OfferStatus.PRIVATE_OFFER_RESTRCITED,
    OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,

    OfferStatus.OFFER_CREATION_FAILED,
    OfferStatus.OFFER_EXPIRED,
    OfferStatus.OFFER_RESTRICTED,
    OfferStatus.OFFER_RESTRICTION_IN_PROGRESS,
  ].includes(offerStatus)

  const hideRecipientStakeholderEditAction = [
    OfferStatus.PRIVATE_OFFER_ACCEPTED,
    OfferStatus.OFFER_ACCEPTED,
  ].includes(offerStatus)

  const handleSubmitRecipient = async (
    offerRecipientsToSend: OfferRecipients[]
  ) => {
    await dispatch(
      updatePrivateOfferForUser(
        privateOffer?.privateOfferId,
        {
          offerRecipients: offerRecipientsToSend,
          ...(cloudMarketplace === 'AZURE' ? { offerStatus } : {}),
        },
        crmOpportunityId,
        false,
        handleCloseRecipients,
        cloudMarketplace,
        routeAccountId
      )
    )
  }

  const handleSubmitStakeholder = async (
    offerStakeholders: OfferStakeholders[],
    customOfferStakeholders: OfferStakeholders[]
  ) => {
    const payload = {
      ...(cloudMarketplace === 'AZURE' ? { offerStatus } : {}),
      offerStakeholders: [
        ...offerStakeholders.map(stakeholder => ({
          firstName: stakeholder.firstName || '',
          lastName: stakeholder.lastName || '',
          email: stakeholder.email,
        })),
        ...customOfferStakeholders.map(stakeholder => ({
          firstName: stakeholder.firstName || '',
          lastName: stakeholder.lastName || '',
          email: stakeholder.email,
        })),
      ],
    }
    await dispatch(
      updatePrivateOfferForUser(
        privateOffer?.privateOfferId,
        payload,
        crmOpportunityId,
        false,
        handleCloseStakeholders,
        cloudMarketplace,
        routeAccountId
      )
    )
  }
  const currencySymbol = getCurrencySymbol(privateOffer?.currency as string)

  return (
    <>
      <div data-testid={'private-offer-details'}>
        <div className={classes.headerContainer}>
          <div className={classes.appTitleHeading}>
            {(isEmpty(externalBaseURL) || onBack) && (
              <div
                className={classes.appBackIcon}
                data-testid="back-button"
                onClick={() =>
                  onBack ? onBack() : navigate(privateOffersListURL)
                }
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  style={{
                    width: '24px',
                    height: '24px',
                    fontSize: '24px',
                    padding: '0px 12px 0px 0px',
                    color: '#1942C5',
                  }}
                />
              </div>
            )}
            <Typography
              variant="h1"
              data-testid="heading"
              className={classes.appTitleHeadingText}
            >
              <ShortenedNameV2
                name={privateOffer?.privateOfferName}
                length={41}
              ></ShortenedNameV2>
            </Typography>
          </div>
          <SelectedCloudIcon />
        </div>
        <div className={classes.root}>
          <Grid container spacing={4}>
            <Grid
              className={classes.leftContainer}
              size={{
                sm: 12,
                md: 8,
                lg: 8,
                xl: 9,
              }}
            >
              <div
                className={classes.statusWrapper}
                data-testid={'offer-header'}
              >
                {cloudMarketplace === 'AWS' && (
                  <AWSOfferHeader
                    privateOffer={privateOffer}
                    planType={planType}
                    crmOpportunityId={crmOpportunityId}
                    externalBaseURL={externalBaseURL}
                    search={search}
                    crmType={crmType}
                    partnerId={routeAccountId}
                    viewTracking={viewTracking}
                  />
                )}
                {cloudMarketplace === 'AZURE' && (
                  <MicrosoftOfferHeader
                    privateOffer={privateOffer}
                    externalBaseURL={externalBaseURL}
                    search={search}
                    partnerId={routeAccountId}
                    viewTracking={viewTracking}
                  />
                )}
              </div>

              {cloudMarketplace === 'AWS' && planType !== PlanType.AMI && (
                <AWSOfferInfo
                  planType={planType}
                  privateOffer={privateOffer}
                  currencySymbol={currencySymbol}
                  partnerId={routeAccountId}
                />
              )}
              {cloudMarketplace === 'AWS' && planType === PlanType.AMI && (
                <AWSAMIOfferInfo
                  privateOffer={privateOffer}
                  currencySymbol={currencySymbol}
                  product={product}
                />
              )}
              {cloudMarketplace === 'AZURE' && (
                <MicrosoftOfferInfo
                  privateOffer={privateOffer}
                  planType={planType}
                  product={product}
                />
              )}

              <EulaView
                eulaHeading={
                  planType === PlanType.AMI ? 'ISV EULA for buyer' : 'EULA type'
                }
                eulaFiles={
                  privateOffer?.eulaFile || privateOffer?.eulaFiles || []
                }
                eulaType={privateOffer?.eulaType}
              />

              {hasOfferMetaData && (
                <CustomMetaDataView metaData={offerMetaData} />
              )}
            </Grid>
            <Grid
              data-testId="offer-stages"
              className={clsx(
                classes.rightContainer,
                classes.stickyStatusListing
              )}
              size={{
                sm: 12,
                md: 4,
                lg: 4,
                xl: 3,
              }}
            >
              {planType !== PlanType?.AMI && (
                <StatusStages
                  cloudMarketplace={cloudMarketplace}
                  privateOffer={privateOffer}
                  planType={planType}
                />
              )}
              <>
                <ViewRecipients
                  offerRecipients={privateOffer?.offerRecipients || []}
                  handleSubmitRecipient={handleSubmitRecipient}
                  setOpenEditRecipient={setOpenEditRecipient}
                  openEditRecipient={openEditRecipient}
                  isUpdateLoading={isLoading}
                  isRecipientEditActionDisabled={isRecipientDisabled}
                  addRecipientsResaleToolTipMessage={
                    !canEditOffer
                      ? permissionMessageString
                      : addRecipientsPrivateOfferToolTipMessage
                  }
                  hideEditActionRecipient={hideRecipientStakeholderEditAction}
                />
                <ViewStakeholders
                  customOfferStakeholders={
                    privateOffer?.customOfferStakeholders || []
                  }
                  offerStakeholders={privateOffer?.offerStakeholders || []}
                  handleSubmitStakeholder={handleSubmitStakeholder}
                  setOpenEditStakeholders={setOpenEditStakeholders}
                  openEditStakeHolders={openEditStakeHolders}
                  isUpdateLoading={isLoading}
                  isStakeholderEditActionDisabled={
                    !canEditOffer || isStakeHolderDisabled
                  }
                  addStakeholderToolTipMessage={
                    !canEditOffer
                      ? permissionMessageString
                      : addStakeholdersPrivateOfferToolTipMessage
                  }
                  hideEditActionStakeholder={hideRecipientStakeholderEditAction}
                  hideStakeholders={!!externalBaseURL}
                  users={users}
                />
              </>
            </Grid>
          </Grid>
        </div>
      </div>
    </>
  )
}
